import {
  DashboardResponse,
  RegistrationDashboardData,
} from '../../../types/dashboard';
import { Users, Shield, Crown, UserCheck } from 'lucide-react';
import { StateIcon } from '../StateIcon';

interface RegistrationPanelProps {
  data: DashboardResponse;
}

/** Map role names to Lucide icons */
function getRoleIcon(role: string) {
  switch (role) {
    case 'Admin': return <Crown size={22} color="#c9a84c" style={{ marginRight: 10, flexShrink: 0 }} />;
    case 'Organizer': return <Shield size={22} color="#c9a84c" style={{ marginRight: 10, flexShrink: 0 }} />;
    case 'Delegate': return <UserCheck size={22} color="#c9a84c" style={{ marginRight: 10, flexShrink: 0 }} />;
    default: return <Users size={22} color="#c9a84c" style={{ marginRight: 10, flexShrink: 0 }} />;
  }
}

const styles = {
  container: {
    padding: 'clamp(16px, 4vw, 48px)',
    overflow: 'hidden' as const,
    height: '100%',
    boxSizing: 'border-box' as const,
    background: '#0d1b12',
    color: '#ffffff',
    fontFamily: "'Inter', system-ui, sans-serif",
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 'clamp(12px, 3vw, 32px)',
  },
  heading: {
    fontSize: 'clamp(24px, 4vw, 48px)',
    color: '#c9a84c',
    margin: 0,
    fontWeight: 700,
  },
  statNumber: {
    fontSize: 'clamp(36px, 6vw, 72px)',
    fontWeight: 800,
    color: '#ffffff',
    margin: 0,
    lineHeight: 1.1,
  },
  statLabel: {
    fontSize: 'clamp(14px, 2vw, 24px)',
    color: '#e0e0e0',
    margin: '8px 0 0 0',
  },
  sectionTitle: {
    fontSize: 'clamp(16px, 2.5vw, 28px)',
    color: '#c9a84c',
    margin: '0 0 12px 0',
    fontWeight: 600,
  },
  listContainer: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 'clamp(16px, 4vw, 48px)',
    flex: 1,
    overflow: 'hidden' as const,
  },
  listColumn: {
    flex: 1,
    overflow: 'hidden' as const,
  },
  listItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    fontSize: 'clamp(14px, 2vw, 24px)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  },
  listItemName: {
    color: '#ffffff',
  },
  listItemCount: {
    color: '#c9a84c',
    fontWeight: 700,
    fontSize: 'clamp(16px, 2.5vw, 28px)',
  },
};

/**
 * Registration area panel — shows total registrations (large stat number),
 * chapter breakdown list, and role breakdown.
 */
export function RegistrationPanel({ data }: RegistrationPanelProps) {
  const regData = data as RegistrationDashboardData;

  return (
    <div style={styles.container}>
      <h1 style={styles.heading}>
        <Users size={42} style={{ marginRight: 16, verticalAlign: 'middle' }} />
        Registration
      </h1>

      <div>
        <p style={styles.statNumber}>{regData.totalRegistrations}</p>
        <p style={styles.statLabel}>Total Registrations</p>
      </div>

      <div style={styles.listContainer}>
        <div style={styles.listColumn}>
          <h2 style={styles.sectionTitle}>By Chapter</h2>
          {regData.registrationsByChapter.map((item) => (
            <div key={item.chapter} style={styles.listItem}>
              <span style={{ ...styles.listItemName, display: 'flex', alignItems: 'center' }}>
                <StateIcon state={item.chapter} size={64} color="#c9a84c" style={{ marginRight: 10 }} />
                {item.chapter}
              </span>
              <span style={styles.listItemCount}>{item.count}</span>
            </div>
          ))}
        </div>

        <div style={styles.listColumn}>
          <h2 style={styles.sectionTitle}>By Role</h2>
          {regData.registrationsByRole.map((item) => (
            <div key={item.role} style={styles.listItem}>
              <span style={{ ...styles.listItemName, display: 'flex', alignItems: 'center' }}>
                {getRoleIcon(item.role)}
                {item.role}
              </span>
              <span style={styles.listItemCount}>{item.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
